import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import { format, parseISO } from 'date-fns';
import scrollIntoView from 'scroll-into-view-if-needed';
import { Image, FileText, Video, FileType, Copy } from 'lucide-react';
import Avatar from '../../../../../clients/shared/Avatar';
import HighlightedMentions from '../../../../../javascript/components/HighlightedMentions';
import TaskDescriptionSnippet from '../../../../../javascript/components/TaskDescriptionSnippet';

import {
  Calendar,
  Tag,
  MousePointer as Arrow,
  Archive,
  Figma
} from 'lucide-react';

import styles from './index.module.css';
import { Tooltip, Checkbox } from 'antd';
import {
  SELECT_TASK_GROUP,
  SELECT_TASK_RANGE,
  wasMultiSelectKeyUsed,
  wasToggleInSelectionGroupKeyUsed
} from '../multiSelect/Util';
import { CommentIndicator } from '../../../../../clients/shared/CommentIndicator/CommentIndicator';
import { accessTo } from 'appJS/models/Access';
import { Sparkles } from '../../../components/Sparkles';
import ProjectSearchMatches, {
  getMatchedTaskUrl
} from '../../../components/ProjectSearchMatches';

const keyCodes = {
  enter: 13
};

type Props = {
  setActiveTask: (id: number) => void;
  action: string;
  isActive: boolean;
  moveTaskToFeedback: (task: any) => void;
  moveTaskToArchive: (task: any) => void;
  getAssignees: (id: number) => any;
  id: number;
  due_at: string;
  local_task_id: number;
  asset_id: number | {};
  tag_ids: number[];
  related_to_task_ids: { merged_from: number[]; merged_to: number[] };
  tags: Object;
  description: string;
  priority_id: number;
  dragHandleProps: any;
  multiSelect: (id: string, type: string) => void;
  unSelectAll: () => void;
  selectedTaskList: Array<string>;
  asset_type: string | Object;
  comments: { mention_ids: number[]; read_by_user_ids: number[] }[];
  hasScreenRecording: boolean;
  viewGuestKanban: boolean;
  canDrag: boolean;
  guestVisible: boolean;
  title?: string;
  ai_suggested_title: boolean;
  original_copy_text?: string | null;
  description_html?: string | null;
  site?: string | null;
  url?: string | null;
};

const getPriorityClass = (priorityId: number) => {
  return (
    [
      styles.notSet,
      styles.critical,
      styles.important,
      styles.normal,
      styles.minor
    ][priorityId] || styles.notSet
  );
};

const KanbanCard: React.FC<Props> = props => {
  const {
    local_task_id,
    asset_id,
    asset_type,
    related_to_task_ids,
    id,
    due_at,
    tags,
    action,
    selectedTaskList,
    tag_ids,
    isActive,
    priority_id,
    comments,
    hasScreenRecording,
    viewGuestKanban,
    canDrag,
    guestVisible,
    original_copy_text
  } = props;

  const cardRef = useRef();

  const isMultiSelectState =
    id &&
    selectedTaskList.length > 1 &&
    selectedTaskList.includes(id.toString());

  const isTextFeedback =
    original_copy_text != null && original_copy_text.trim() !== '';

  const projectSearchResults = window.bugherd?.application.get(
    'projectSearchResults'
  );
  const searchMatches = projectSearchResults?.matchesByTaskId[id] || [];
  const descriptionSearchHighlight =
    !props.title && searchMatches.includes('description')
      ? projectSearchResults.query
      : undefined;
  const matchedAssignees =
    projectSearchResults?.matchedAssigneesByTaskId[id] || [];
  const matchedComments =
    projectSearchResults?.matchedCommentsByTaskId[id] || [];
  const pageQuery = projectSearchResults?.pageQuery || '';
  const matchedUrl = getMatchedTaskUrl(
    searchMatches,
    props.site,
    props.url,
    pageQuery
  );

  const scrollToCard = ({ animate }: { animate: boolean }) => {
    setTimeout(() => {
      if (cardRef.current) {
        // Using parentElement is no good, but because of the styling (margin/padding)
        // and the autogenerated elements from drag and drop, it's a good enough solution
        // @ts-expect-error
        scrollIntoView(cardRef.current.parentElement, {
          behavior: animate ? 'smooth' : 'auto'
        });
      }
    }, 100);
  };

  useEffect(() => {
    if (isActive && !isMultiSelectState) scrollToCard({ animate: false });
  }, []);

  useEffect(() => {
    if (isActive && !isMultiSelectState) scrollToCard({ animate: true });
  }, [isActive]);

  const moveToFeedback = (event, task) => {
    event.stopPropagation();
    props.moveTaskToFeedback(task);
  };

  const moveToArchive = (event, task) => {
    event.stopPropagation();
    props.moveTaskToArchive(task);
  };

  const currentUser = window.bugherd?.application.currentUser();

  const getAssignees = () => {
    return props
      .getAssignees(id)
      .filter(
        ({ role }) => currentUser.get('role') !== 'guest' || role === 'guest'
      )
      .slice(0, 3)
      .map(user => {
        return {
          name: user.name,
          email: user.email,
          avatar: user.avatar,
          role: user.role,
          firstName: user.first_name,
          surname: user.surname,
          id: user.id
        };
      });
  };

  const performAction = (event, checkboxPresent) => {
    const { setActiveTask, multiSelect, unSelectAll, id } = props;

    if (wasMultiSelectKeyUsed(event)) {
      multiSelect(id.toString(), SELECT_TASK_RANGE);
    } else if (wasToggleInSelectionGroupKeyUsed(event) || checkboxPresent) {
      multiSelect(id.toString(), SELECT_TASK_GROUP);
    } else {
      setActiveTask(id);
      unSelectAll();
    }
  };

  const onCardClick = event => {
    event.preventDefault();
    performAction(event, null);
  };

  const onCheckboxClick = event => {
    event.preventDefault();
    event.stopPropagation();
    performAction(event, 'checkboxPresent');
  };

  const onKeyDown = event => {
    if (event.defaultPrevented) {
      return;
    }

    if (event.keyCode !== keyCodes.enter) {
      return;
    }
    event.preventDefault();
    performAction(event, undefined);
  };

  const showAssignedGuests =
    accessTo('assign_guests_allowed') && accessTo('assign_guests');

  const assignees = getAssignees().filter(
    ({ id, role }) =>
      !viewGuestKanban ||
      (showAssignedGuests &&
        ((guestVisible && role === 'guest') || currentUser.id === id))
  );

  const tagsString = (tag_ids || [])
    .map(tagId => (tags && tags[tagId] ? tags[tagId].get('name') : ''))
    .filter(name => name && name !== '')
    .join(', ');

  const unreadCommentsEnabled = true;
  const getContainer = () =>
    document.getElementById('kanbanBoard') as HTMLDivElement;

  const renderTextFeedbackDescription = () => (
    <>
      <p className={styles.textFeedbackHeading}>Replace:</p>
      <HighlightedMentions
        {...{
          description: original_copy_text!,
          className: cx(styles.mentionText)
        }}
        includeGuests={showAssignedGuests}
      />
    </>
  );

  const hasTitle = !!props.title?.trim();
  const taskTitleHoverText = hasTitle
    ? props.title.trim()
    : props.description?.trim();

  const renderRegularDescription = () => (
    <TaskDescriptionSnippet
      {...{
        title: props.title,
        description: props.description,
        descriptionHtml: props.description_html,
        variant: 'kanban',
        className: cx(styles.mentionText, {
          [styles.aiSuggestedTitle]: hasTitle && props.ai_suggested_title,
          [styles.withTitle]: hasTitle
        })
      }}
      includeGuests={showAssignedGuests}
      searchHighlightText={descriptionSearchHighlight}
      icon={
        hasTitle && props.ai_suggested_title ? (
          <Tooltip title="AI Suggestion" getPopupContainer={getContainer}>
            <sup className={styles.sparklesIconContainer}>
              <Sparkles
                className={styles.sparklesIcon}
                linearGradient
                size={10}
              />
            </sup>
          </Tooltip>
        ) : null
      }
    />
  );

  return (
    <div
      className={styles.cardOuter}
      ref={cardRef}
      {...props.dragHandleProps}
      data-intercom-target="Task"
      data-task-id={id}
      data-performance-event="KanbanCard"
      onClick={e => {
        e.currentTarget.focus();
        onCardClick(e);
      }}
    >
      <div
        className={cx({
          [styles.card]: true,
          [styles.activeCard]: isActive,
          [styles.activeCardMultiSelect]: isMultiSelectState,
          [styles.activeCardMultiSelectBG]: isMultiSelectState
        })}
        onKeyDown={event => onKeyDown(event)}
        data-performance-event="KanbanCard"
      >
        <div
          className={cx([
            styles.priorityIndicator,
            getPriorityClass(priority_id)
          ])}
        />
        <div
          className={styles.cardInnerContent}
          data-performance-event="KanbanCard"
        >
          <div
            className={cx(
              {
                [styles.hasAction]: !!action
              },
              styles.metadataContainer
            )}
          >
            <div className={styles.idCheckboxWrapper}>
              <span className={styles.idInfo}>
                {local_task_id && `#${local_task_id}`}

                {related_to_task_ids?.merged_from.length > 0 && (
                  <span className={styles.mergedIcon}>
                    <Copy className={styles.copyIcon} />
                    MERGED
                  </span>
                )}

                {asset_id && (
                  <span className={styles.assetIcon}>
                    {asset_type === 'figma' && (
                      <>
                        <Figma className={styles.imageIcon} />
                        FIGMA
                      </>
                    )}
                    {asset_type === 'pdf' && (
                      <>
                        <FileText className={styles.fileIcon} />
                        PDF
                      </>
                    )}
                    {asset_type !== 'figma' && asset_type !== 'pdf' && (
                      <>
                        <Image className={styles.imageIcon} />
                        IMAGE
                      </>
                    )}
                  </span>
                )}
                {isTextFeedback && (
                  <span className={styles.assetIcon}>
                    <FileType className={styles.fileIcon} />
                    COPY
                  </span>
                )}
              </span>

              {action && canDrag && (
                <Tooltip
                  title={`Move task to ${action.slice(2)}`}
                  placement={action.includes('Feedback') ? 'right' : 'left'}
                  getPopupContainer={getContainer}
                >
                  {action.includes('Feedback') ? (
                    <Arrow
                      onClick={event => moveToFeedback(event, { id })}
                      className={styles.cardActionIcon}
                    />
                  ) : (
                    <Archive
                      onClick={event => moveToArchive(event, { id })}
                      className={styles.cardActionIcon}
                    />
                  )}
                </Tooltip>
              )}

              <Checkbox
                data-performance-event="BulkActionsCheckbox"
                className={styles.checkboxMultiselect}
                checked={isActive}
                onClick={onCheckboxClick}
              />
            </div>
          </div>
          <div
            className={styles.description}
            data-performance-event="KanbanCard"
            title={
              isTextFeedback ? undefined : taskTitleHoverText || undefined
            }
          >
            {isTextFeedback
              ? renderTextFeedbackDescription()
              : renderRegularDescription()}
          </div>
          {unreadCommentsEnabled && (
            <CommentIndicator
              // @ts-expect-error
              comments={comments}
              currentUserId={currentUser.get('id')}
            />
          )}
          <ProjectSearchMatches
            assignees={matchedAssignees}
            comments={matchedComments}
            includeGuests={showAssignedGuests}
            query={projectSearchResults?.query || ''}
            url={matchedUrl}
            urlQuery={pageQuery || projectSearchResults?.query || ''}
          />
        </div>

        <div
          className={cx({
            [styles.activeCardMultiSelectBG]: isMultiSelectState,
            [styles.taskInfoFooter]: true
          })}
        >
          {due_at && (
            <div className={styles.dueBox}>
              <Calendar className={styles.dueIcon} />
              {format(parseISO(due_at), 'd MMM yyyy')}
            </div>
          )}
          {tag_ids.length > 0 && (
            <div className={styles.tagBox} title={`tags: ${tagsString}`}>
              <Tag className={styles.tagIcon} />
              <span className={styles.tags}>{tagsString}</span>
            </div>
          )}
          {hasScreenRecording && <Video className={styles.videoIcon} />}
          {!!assignees?.length && (
            <div className={styles.assigneesContainer}>
              {assignees.map(assignee => {
                return (
                  <div
                    className={styles.avatarContainer}
                    key={assignee.email}
                    title={assignee.email}
                  >
                    <Avatar member={assignee} size="small" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;
